import clsx from "clsx";
import React, { useEffect, useMemo } from "react";
import { formatCurrency, rgbaObjectToString } from "../../helpers/utils";
import useFilteredTransactions from "../../hooks/useFilteredTransactions";
import { sumBy, groupBy, map, flatten, filter } from "lodash-es";
import CommonBarLineChart from "../Charts/CommonBarLineChart";
import { ICellRendererParams } from "ag-grid-community";
import useColors from "../../hooks/useColors";
import { ETransactionType } from "../../helpers/constants";
import CommonTable from "../Tables/CommonTable";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import useMainFilter from "../../hooks/useMainFilter";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import userPreferencesStore from "../../store/userPreferences";
import useCollapsibles from "../../hooks/useCollapsibles";
import useCharts from "../../hooks/useCharts";
import usePinnedColumns from "src/hooks/usePinnedColumns";
import useMobile from "src/hooks/useMobile";
import useAllTransactions from "src/hooks/useAllTransactions";
import { useNavigate } from "react-router-dom";
import { transactionStatus } from "src/helpers/transactions";
import { EReportsTableKeys } from "../Tables/tableIds";
import { EReportsChartKeys } from "../Charts/chartIds";
import { useTranslation } from "react-i18next";

interface IReportByStatus {
  onSelectionChange: (rows: Array<any>) => void;
  onDataChange: (data: any) => void;
}

const ReportByStatus: React.FC<IReportByStatus> = ({
  onSelectionChange,
  onDataChange,
}) => {
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  const { getSourceCustomSettings } = useAllTransactions();
  const {
    collapsiblePreferences,
    updateCollapsible,
    isStatsMobileCollapsibleOpen,
    toggleStatsMobileCollapsible,
  } = useCollapsibles();
  const { chartPreferences } = useCharts();
  const { updatePinnedColumns, isPinnedColumn } = usePinnedColumns();
  const { filteredTransactions } = useFilteredTransactions();
  const { colors } = useColors();
  const { currencySymbol } = useCurrentAccount();
  const { selectedYear } = userPreferencesStore((state: any) => ({
    selectedYear: state.selectedYear,
  }));

  const { mainFilter, setMainFilter } = useMainFilter();
  const navigate = useNavigate();

  const seriesColors = useMemo(
    () => ({
      income: colors?.income?.bg,
      expenses: colors?.expenses?.bg,
    }),
    [colors?.income?.bg, colors?.expenses?.bg]
  );

  const columns: any = useMemo(
    () => [
      {
        field: "status",
        flex: 1,
        minWidth: 200,
        headerName: t("table_headings.status"),
        cellRenderer: "CellWithBackgroundRenderer",
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.statusFormatted,
        cellRendererParams: (params: ICellRendererParams) => {
          const source = getSourceCustomSettings(params?.data?.statusFormatted);
          return {
            params,
            custom: {
              background: rgbaObjectToString(source?.bg),
              color: rgbaObjectToString(source?.color),
              onClick: () => {
                setMainFilter({
                  status: [params?.data?.status],
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        pinned: isPinnedColumn("clientProvidersReportClientProviderPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                clientProvidersReportClientProviderPinned: isPinned,
              });
            },
          },
        },
      },
      {
        minWidth: 170,
        field: "income",
        suppressSizeToFit: true,
        headerName: t("table_headings.income"),
        pinned: isPinnedColumn("clientProvidersReportIncomePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                clientProvidersReportIncomePinned: isPinned,
              });
            },
          },
        },
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.incomeFormatted,
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: colors.income.bg,
              color: colors.income.color,
              onClick: () => {
                setMainFilter({
                  status: [params?.data?.status],
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        hide: mainFilter?.type === ETransactionType.EXPENSE,
      },
      {
        minWidth: 170,
        field: "expenses",
        suppressSizeToFit: true,
        headerName: t("table_headings.expenses"),
        pinned: isPinnedColumn("clientProvidersReportExpensesPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                clientProvidersReportExpensesPinned: isPinned,
              });
            },
          },
        },
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.expensesFormatted,
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: colors.expenses.bg,
              color: colors.expenses.color,
              onClick: () => {
                setMainFilter({
                  status: [params?.data?.status],
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        hide: mainFilter?.type === ETransactionType.INCOME,
      },
      {
        minWidth: 170,
        hide: !!mainFilter?.type,
        suppressSizeToFit: true,
        field: "formattedDifference",
        headerName: t("table_headings.diff"),
        pinned: isPinnedColumn("clientProvidersReportDifferencePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                clientProvidersReportDifferencePinned: isPinned,
              });
            },
          },
        },
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.differenceFormatted,
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const valueIsNegative = params?.data?.difference < 0;

          return {
            params,
            custom: {
              background: valueIsNegative
                ? colors.difference.negative.bg
                : colors.difference.positive.bg,
              color: valueIsNegative
                ? colors.difference.negative.color
                : colors.difference.positive.color,
              onClick: () => {
                setMainFilter({
                  status: [params?.data?.status],
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
      },
    ],
    [
      colors?.expenses?.bg,
      colors?.expenses?.color,
      colors?.income?.bg,
      colors?.income?.color,
      mainFilter?.type,
      colors?.difference?.positive?.color,
      colors?.difference?.positive?.bg,
      colors?.difference?.negative?.color,
      colors?.difference?.negative?.bg,
      isPinnedColumn,
      t,
    ]
  );

  const groupedData = useMemo(() => {
    // Filtra las transacciones por el año seleccionado usando lodash
    const filteredByYear = selectedYear
      ? filter(filteredTransactions, (transaction) => {
          const transactionYear = new Date(transaction.date).getFullYear();
          return transactionYear === selectedYear;
        })
      : filteredTransactions;

    return map(groupBy(filteredByYear, "status"), (transactions, status) => {
      const income = sumBy(transactions, (t) =>
        t.type === ETransactionType.INCOME ? Number(t.total) : 0
      );
      const expenses = sumBy(transactions, (t) =>
        t.type === ETransactionType.EXPENSE ? Number(t.total) : 0
      );

      return {
        statusFormatted: transactionStatus.getNameById(status) || "Desconocido",
        status: status,
        income,
        incomeFormatted: formatCurrency({
          value: income,
          currency: currencySymbol,
        }),
        expenses,
        expensesFormatted: formatCurrency({
          value: expenses,
          currency: currencySymbol,
        }),
        difference: income - expenses, // Nueva propiedad: diferencia entre ingresos y gastos
        differenceFormatted: formatCurrency({
          value: income - expenses,
          currency: currencySymbol,
        }),
      };
    });
  }, [filteredTransactions, currencySymbol, selectedYear]);

  const chartData = useMemo(() => {
    const common = {
      type: chartPreferences.type,
      smooth: true,
      symbolSize: 20, // Tamaño de los puntos
      lineStyle: {
        width: 2, // Grosor de la línea
      },
    };
    return [
      {
        ...common,
        name: t("transaction_types.incomes"),
        data: flatten(map(groupedData, "income")),
        itemStyle: {
          color: seriesColors?.income,
          borderRadius: [6, 6, 0, 0], // [topLeft, topRight, bottomRight, bottomLeft]
        },
        show: mainFilter?.type !== ETransactionType.EXPENSE,
        large: true,
      },
      {
        ...common,
        name: t("transaction_types.expenses"),
        data: flatten(map(groupedData, "expenses")),
        itemStyle: {
          color: seriesColors?.expenses,
          borderRadius: [6, 6, 0, 0], // [topLeft, topRight, bottomRight, bottomLeft]
        },
        show: mainFilter?.type !== ETransactionType.INCOME,
        large: true,
      },
    ].filter(({ show }) => show);
  }, [
    groupedData,
    seriesColors?.expenses,
    seriesColors?.income,
    mainFilter?.type,
  ]);

  const categories = useMemo(
    () => map(groupedData, "statusFormatted"),
    [groupedData]
  );

  useEffect(() => {
    onDataChange(groupedData);
  }, [groupedData]);

  return (
    <div className={clsx("flex w-full flex-col gap-4")}>
      <CollapsibleBlock
        title={t("common.chart")}
        open={collapsiblePreferences.statsChartOpen}
        onChange={(isOpen: boolean) => {
          updateCollapsible({ statsChartOpen: isOpen });
        }}
        uppercase
      >
        <CommonBarLineChart
          categories={categories}
          series={chartData}
          chartId={EReportsChartKeys.STATUS_TRANSACTIONS}
        />
      </CollapsibleBlock>
      <CollapsibleBlock
        title={t("common.table")}
        open={collapsiblePreferences.statsTableOpen}
        onChange={(isOpen: boolean) => {
          updateCollapsible({ statsTableOpen: isOpen });
        }}
        uppercase
      >
        <CommonTable
          columns={columns}
          data={groupedData}
          onSelectionChange={onSelectionChange}
          settingsKey={EReportsTableKeys.STATUS_TRANSACTIONS}
        />
      </CollapsibleBlock>
    </div>
  );
};

export default React.memo(ReportByStatus);
